import { useState, useEffect, useCallback } from "react";
import { Select } from "antd";
import { useOutletContext } from "react-router-dom";
import { roles as rolesApi, dataPermissions } from "../../../../services/admin";
import { ensurePermission } from "../../../../utils/permissions";
import type { RoleItem, DataPermissionRule } from "../../../../services/admin";
import RIcon from "../../../../components/RIcon";

// ── Scope type value mapping ──

const scopeTypeMap: Record<string, string> = {
  all: "全公司",
  public: "全公司",
  self: "仅本人",
  user: "指定用户",
  department: "指定部门",
  job_type: "指定工种",
  role: "指定角色",
  custom_user: "指定用户",
  custom_dept: "指定部门",
  custom_job: "指定工种",
  custom_role: "指定角色",
};

// ── Scope type display config ──

const scopeTypeColors: Record<string, string> = {
  "全公司": "bg-emerald-50 text-emerald-600",
  "仅本人": "bg-amber-50 text-amber-600",
  "指定用户": "bg-violet-50 text-violet-600",
  "指定部门": "bg-sky-50 text-sky-600",
  "指定工种": "bg-orange-50 text-orange-600",
  "指定角色": "bg-rose-50 text-rose-600",
};

function getScopeColor(scope: string): string {
  return scopeTypeColors[scope] || "bg-gray-100 text-gray-500";
}

// ── Create Rule Modal ──

interface CreateRuleModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  mode?: "create" | "edit";
  initialRule?: DataPermissionRule | null;
}

function CreateRuleModal({ open, onClose, onSaved, mode = "create", initialRule = null }: CreateRuleModalProps) {
  const [name, setName] = useState("");
  const [scopeType, setScopeType] = useState("all");
  const [relatedObjects, setRelatedObjects] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Custom scope selections
  const [userList, setUserList] = useState<string[]>([]);
  const [deptList, setDeptList] = useState<string[]>([]);
  const [jobTypeList, setJobTypeList] = useState<string[]>([]);
  const [roleList, setRoleList] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedDepts, setSelectedDepts] = useState<string[]>([]);
  const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [customLoading, setCustomLoading] = useState(false);

  const isCustom = scopeType.startsWith("custom_");

  // Load lists when scope changes to any custom type
  useEffect(() => {
    if (
      isCustom &&
      userList.length === 0 &&
      deptList.length === 0 &&
      jobTypeList.length === 0 &&
      roleList.length === 0
    ) {
      setCustomLoading(true);
      Promise.all([
        dataPermissions.getUsers(),
        dataPermissions.getDepartments(),
        dataPermissions.getJobTypes(),
        dataPermissions.getRoles(),
      ])
        .then(([u, d, j, r]) => {
          setUserList(u);
          setDeptList(d);
          setJobTypeList(j);
          setRoleList(r);
        })
        .catch(() => setError("加载选项失败"))
        .finally(() => setCustomLoading(false));
    }
  }, [isCustom, userList.length, deptList.length, jobTypeList.length, roleList.length]);

  useEffect(() => {
    if (!open) return;
    const backToFrontScopeMap: Record<string, string> = {
      public: "all",
      all: "all",
      self: "self",
      user: "custom_user",
      department: "custom_dept",
      job_type: "custom_job",
      role: "custom_role",
    };
    if (mode === "edit" && initialRule) {
      const mappedScope = backToFrontScopeMap[initialRule.scope_type] || initialRule.scope_type;
      const values = (initialRule.related_objects || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
      setName(initialRule.name);
      setScopeType(mappedScope);
      setRelatedObjects(initialRule.related_objects || "");
      setSelectedUsers(mappedScope === "custom_user" ? values : []);
      setSelectedDepts(mappedScope === "custom_dept" ? values : []);
      setSelectedJobTypes(mappedScope === "custom_job" ? values : []);
      setSelectedRoles(mappedScope === "custom_role" ? values : []);
      setError("");
      return;
    }
    setName("");
    setScopeType("all");
    setRelatedObjects("");
    setSelectedUsers([]);
    setSelectedDepts([]);
    setSelectedJobTypes([]);
    setSelectedRoles([]);
    setError("");
  }, [open, mode, initialRule]);

  const toggleItem = (
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    item: string
  ) => {
    setList(list.includes(item) ? list.filter((v) => v !== item) : [...list, item]);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("请输入规则名称");
      return;
    }

    // Build related_objects — 后端按逗号分割取纯名称
    let finalRelated = relatedObjects.trim();
    if (scopeType === "custom_user" && selectedUsers.length > 0) {
      finalRelated = selectedUsers.join(",");
    } else if (scopeType === "custom_dept" && selectedDepts.length > 0) {
      finalRelated = selectedDepts.join(",");
    } else if (scopeType === "custom_job" && selectedJobTypes.length > 0) {
      finalRelated = selectedJobTypes.join(",");
    } else if (scopeType === "custom_role" && selectedRoles.length > 0) {
      finalRelated = selectedRoles.join(",");
    }

    setSubmitting(true);
    setError("");
    try {
      // 映射前端 scopeType 到后端期望的值
      const backendScopeMap: Record<string, string> = {
        all: "public",
        self: "self",
        custom_user: "user",
        custom_dept: "department",
        custom_job: "job_type",
        custom_role: "role",
      };
      const payload = {
        name: name.trim(),
        scope_type: backendScopeMap[scopeType] || scopeType,
        related_objects: finalRelated || undefined,
      };
      if (mode === "edit" && initialRule) {
        await dataPermissions.updateRule(initialRule.id, payload);
      } else {
        await dataPermissions.createRule(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : mode === "edit" ? "保存失败" : "创建失败");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-4 sm:p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-sm font-bold text-[#1A2B3C]">{mode === "edit" ? "编辑数据权限规则" : "新建数据权限规则"}</h3>

        {error && (
          <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
        )}

        <div>
          <label className="text-xs font-medium text-[#718096] block mb-1">规则名称</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如：部门文件读取权限"
            className="w-full h-9 px-3 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#0EA5A0] transition-colors"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-[#718096] block mb-1">数据范围</label>
          <Select
            value={scopeType}
            onChange={(v) => setScopeType(v as string)}
            style={{ width: "100%" }}
            options={[
              { value: "all", label: "全公司" },
              { value: "self", label: "仅本人" },
              { value: "custom_user", label: "指定用户" },
              { value: "custom_dept", label: "指定部门" },
              { value: "custom_job", label: "指定工种" },
              { value: "custom_role", label: "指定角色" },
            ]}
          />
        </div>

        {isCustom && (
          <div>
            {customLoading ? (
              <div className="flex items-center justify-center py-4">
                <RIcon name="loader-4-line" className="animate-spin text-lg text-[#0EA5A0]" />
                <span className="ml-2 text-xs text-[#718096]">加载选项...</span>
              </div>
            ) : (
              <>
                {scopeType === "custom_user" && (
                  <div>
                    <label className="text-xs font-medium text-[#718096] block mb-1.5">
                      选择用户 {selectedUsers.length > 0 && <span className="text-[#0EA5A0]">({selectedUsers.length})</span>}
                    </label>
                    <div className="border border-gray-200 rounded-lg p-2 max-h-40 overflow-y-auto">
                      {userList.length === 0 ? <p className="text-xs text-[#718096] text-center py-2">暂无用户</p> : (
                        <div className="grid grid-cols-2 gap-1">
                          {userList.map((u) => (
                            <label key={u} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50">
                              <input type="checkbox" className="accent-[#0EA5A0]" checked={selectedUsers.includes(u)} onChange={() => toggleItem(selectedUsers, setSelectedUsers, u)} />
                              <span className="text-xs text-[#4A5568] truncate">{u}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {scopeType === "custom_dept" && (
                  <div>
                    <label className="text-xs font-medium text-[#718096] block mb-1.5">
                      选择部门 {selectedDepts.length > 0 && <span className="text-[#0EA5A0]">({selectedDepts.length})</span>}
                    </label>
                    <div className="border border-gray-200 rounded-lg p-2 max-h-40 overflow-y-auto">
                      {deptList.length === 0 ? <p className="text-xs text-[#718096] text-center py-2">暂无部门</p> : (
                        <div className="grid grid-cols-2 gap-1">
                          {deptList.map((d) => (
                            <label key={d} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50">
                              <input type="checkbox" className="accent-[#0EA5A0]" checked={selectedDepts.includes(d)} onChange={() => toggleItem(selectedDepts, setSelectedDepts, d)} />
                              <span className="text-xs text-[#4A5568] truncate">{d}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {scopeType === "custom_job" && (
                  <div>
                    <label className="text-xs font-medium text-[#718096] block mb-1.5">
                      选择工种 {selectedJobTypes.length > 0 && <span className="text-[#0EA5A0]">({selectedJobTypes.length})</span>}
                    </label>
                    <div className="border border-gray-200 rounded-lg p-2 max-h-40 overflow-y-auto">
                      {jobTypeList.length === 0 ? <p className="text-xs text-[#718096] text-center py-2">暂无工种</p> : (
                        <div className="grid grid-cols-2 gap-1">
                          {jobTypeList.map((j) => (
                            <label key={j} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50">
                              <input type="checkbox" className="accent-[#0EA5A0]" checked={selectedJobTypes.includes(j)} onChange={() => toggleItem(selectedJobTypes, setSelectedJobTypes, j)} />
                              <span className="text-xs text-[#4A5568] truncate">{j}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {scopeType === "custom_role" && (
                  <div>
                    <label className="text-xs font-medium text-[#718096] block mb-1.5">
                      选择角色 {selectedRoles.length > 0 && <span className="text-[#0EA5A0]">({selectedRoles.length})</span>}
                    </label>
                    <div className="border border-gray-200 rounded-lg p-2 max-h-40 overflow-y-auto">
                      {roleList.length === 0 ? <p className="text-xs text-[#718096] text-center py-2">暂无角色</p> : (
                        <div className="grid grid-cols-2 gap-1">
                          {roleList.map((r) => (
                            <label key={r} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50">
                              <input type="checkbox" className="accent-[#0EA5A0]" checked={selectedRoles.includes(r)} onChange={() => toggleItem(selectedRoles, setSelectedRoles, r)} />
                              <span className="text-xs text-[#4A5568] truncate">{r}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!isCustom && (
          <div>
            <label className="text-xs font-medium text-[#718096] block mb-1">关联对象（可选）</label>
            <input
              type="text"
              value={relatedObjects}
              onChange={(e) => setRelatedObjects(e.target.value)}
              placeholder="例如：文件库, 项目数据"
              className="w-full h-9 px-3 text-sm border border-gray-200 rounded-lg outline-none focus:border-[#0EA5A0] transition-colors"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 h-8 text-xs border border-gray-200 text-[#718096] hover:border-gray-300 rounded-lg cursor-pointer"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 h-8 text-xs bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg cursor-pointer disabled:opacity-50"
          >
            {submitting ? (mode === "edit" ? "保存中..." : "创建中...") : (mode === "edit" ? "保存" : "创建")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──

export default function DataPermissionsPage() {
  const { user } = useOutletContext<{ user: import("../../../../services/auth").UserInfo | null }>();
  const [rolesList, setRolesList] = useState<RoleItem[]>([]);
  const [rules, setRules] = useState<DataPermissionRule[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editRule, setEditRule] = useState<DataPermissionRule | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const loadData = useCallback(async () => {
    try {
      const [rolesData, rulesData] = await Promise.all([
        rolesApi.list(),
        dataPermissions.listRules(),
      ]);
      setRolesList(rolesData);
      setRules(rulesData);
      if (rolesData.length > 0 && selectedRoleId === null) {
        setSelectedRoleId(rolesData[0].id);
      }
    } catch (err) {
      console.error("Failed to load data permissions:", err);
      showToast("加载数据失败");
    } finally {
      setLoading(false);
    }
  }, [selectedRoleId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteRule = async (ruleId: number) => {
    try {
      await dataPermissions.deleteRule(ruleId);
      setRules((prev) => prev.filter((r) => r.id !== ruleId));
      showToast("规则已删除");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "删除失败");
    }
  };

  const handleRuleCreated = () => {
    showToast("规则已创建");
    loadData();
  };

  const selectedRole = rolesList.find((r) => r.id === selectedRoleId);

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="h-8 w-48 bg-gray-100 rounded animate-pulse" />
        <div className="flex gap-4">
          <div className="w-52 h-64 bg-gray-100 rounded-xl animate-pulse" />
          <div className="flex-1 h-64 bg-gray-100 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#0EA5A0] text-white text-sm px-4 py-2.5 rounded-lg shadow-md">
          <RIcon name="checkbox-circle-line" className="mr-2" />{toast}
        </div>
      )}

      <CreateRuleModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={handleRuleCreated}
      />

      <CreateRuleModal
        open={!!editRule}
        onClose={() => setEditRule(null)}
        onSaved={() => {
          setEditRule(null);
          handleRuleCreated();
        }}
        mode="edit"
        initialRule={editRule}
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-[#1A2B3C]">数据权限管理</h1>
          <p className="text-sm text-[#718096] mt-0.5">管理数据权限规则，控制不同范围的数据访问</p>
        </div>
        <button
          onClick={() => ensurePermission(user, "data_permissions:write", showToast) && setCreateOpen(true)}
          className="flex items-center gap-2 px-4 h-9 bg-[#0EA5A0] hover:bg-[#0D9590] text-white text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap"
        >
          <RIcon name="add-line" />新建规则
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Role List */}
        <div className="w-full lg:w-52 lg:flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-semibold text-[#718096]">角色列表</p>
          </div>
          <div className="p-2 flex flex-row lg:flex-col gap-1 lg:gap-0 lg:space-y-1 overflow-x-auto lg:overflow-visible">
            {rolesList.length === 0 ? (
              <p className="text-xs text-[#718096] px-3 py-4 text-center w-full">暂无角色</p>
            ) : (
              rolesList.map((role) => (
                <button
                  key={role.id}
                  onClick={() => setSelectedRoleId(role.id)}
                  className={`flex-shrink-0 lg:flex-shrink lg:w-full text-left px-3 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap lg:whitespace-normal ${
                    selectedRoleId === role.id ? "bg-[#0EA5A0]/10 text-[#0EA5A0]" : "hover:bg-gray-50 text-[#4A5568]"
                  }`}
                >
                  <p className="text-sm font-medium truncate">{role.name}</p>
                  <p className="text-xs text-[#718096]">{role.user_count} 人</p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Data Permission Rules Table */}
        <div className="flex-1 min-w-0 bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-5 py-3.5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="min-w-0">
              <span className="text-sm font-semibold text-[#1A2B3C]">
                {selectedRole ? selectedRole.name : "数据权限规则"}
              </span>
              {selectedRole && (
                <span className="ml-2 text-xs text-[#718096]">
                  {selectedRole.description ? `· ${selectedRole.description}` : ""} · 共 {selectedRole.user_count} 人
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#718096]">共 {rules.length} 条规则</span>
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F4F8FB]">
                  <th className="text-left px-5 py-3 text-xs font-medium text-[#718096]">规则名称</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#718096]">数据范围</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#718096]">关联对象</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-[#718096]">关联账户</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-[#718096]">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rules.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <RIcon name="shield-line" className="text-3xl text-gray-300" />
                        <p className="text-sm text-[#718096]">暂无数据权限规则</p>
                        <button
                          onClick={() => ensurePermission(user, "data_permissions:write", showToast) && setCreateOpen(true)}
                          className="text-xs text-[#0EA5A0] hover:underline cursor-pointer"
                        >
                          点击创建第一条规则
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rules.map((rule) => {
                    const scopeLabel = scopeTypeMap[rule.scope_type] || rule.scope_type;
                    return (
                      <tr key={rule.id} className="hover:bg-[#F4F8FB]/50 transition-colors">
                        <td className="px-5 py-3.5">
                          <span className="text-sm font-medium text-[#1A2B3C]">{rule.name}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`text-xs px-2 py-1 rounded-full ${getScopeColor(scopeLabel)}`}>
                            {scopeLabel}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-[#4A5568]">
                            {rule.related_objects || "-"}
                          </span>
                        </td>
                        <td className="text-center px-4 py-3.5">
                          <span className="text-sm text-[#4A5568]">{rule.account_count}</span>
                        </td>
                        <td className="text-center px-4 py-3.5">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => ensurePermission(user, "data_permissions:write", showToast) && setEditRule(rule)}
                              className="w-7 h-7 inline-flex items-center justify-center text-[#718096] hover:text-[#0EA5A0] hover:bg-[#0EA5A0]/10 rounded-lg transition-colors cursor-pointer"
                              title="编辑规则"
                            >
                              <RIcon name="edit-line" className="text-sm" />
                            </button>
                            {/* 数据权限规则禁止删除（避免误删致大量文件可见性变化） */}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="sm:hidden divide-y divide-gray-100">
            {rules.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <div className="flex flex-col items-center gap-2">
                  <RIcon name="shield-line" className="text-3xl text-gray-300" />
                  <p className="text-sm text-[#718096]">暂无数据权限规则</p>
                  <button
                    onClick={() => ensurePermission(user, "data_permissions:write", showToast) && setCreateOpen(true)}
                    className="text-xs text-[#0EA5A0] hover:underline cursor-pointer"
                  >
                    点击创建第一条规则
                  </button>
                </div>
              </div>
            ) : (
              rules.map((rule) => {
                const scopeLabel = scopeTypeMap[rule.scope_type] || rule.scope_type;
                return (
                  <div key={rule.id} className="px-4 py-3.5 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#1A2B3C] truncate">{rule.name}</p>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getScopeColor(scopeLabel)}`}>
                            {scopeLabel}
                          </span>
                          <span className="text-xs text-[#718096]">{rule.account_count} 人</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => ensurePermission(user, "data_permissions:write", showToast) && setEditRule(rule)}
                          className="w-8 h-8 inline-flex items-center justify-center text-[#718096] hover:text-[#0EA5A0] hover:bg-[#0EA5A0]/10 rounded-lg cursor-pointer"
                          title="编辑规则"
                        >
                          <RIcon name="edit-line" className="text-sm" />
                        </button>
                        {/* 数据权限规则禁止删除 */}
                      </div>
                    </div>
                    {rule.related_objects && (
                      <p className="text-xs text-[#4A5568] break-all">
                        <span className="text-[#718096]">关联对象：</span>
                        {rule.related_objects}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
